import { authRedirectUrl } from 'presentations/routes/helper';
import {
  accountSettingPath,
  apiPath,
  calendarIntegrationsPath,
  dashboardCalendarsPath,
  dashboardPath,
  dashboardSearchPath,
  dashboardStatsPath,
  helpCenterPath,
  homePath,
  hostEventTypeListPath,
  hostEventTypeSettingPath,
  integrationListPath,
  integrationPath,
  messagesPath,
  notificationsPath,
  scheduledEventListPath,
  serviceTermListPath,
} from 'presentations/routes/path';
import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useChat } from 'react-live-chat-loader';
import {
  isInElectron,
  isMacintosh as isMacintoshFunc,
  isiOSApp as isiOSAppFunc,
} from 'presentations/views/helpers/electron';

import {
  DashboardPortalContext,
  setProjects,
} from 'presentations/reducers/DashboardPortal';
import { Router, useRouter } from 'next/router';
import { ProjectListItem } from 'presentations/api/schema/project';
import { useProjectForm } from '../ProjectForm/hooks';
import { useRequest } from 'presentations/views/hooks/request';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  AssistantWidgetContext,
  setChatPopoverAnchor,
} from 'presentations/reducers/AssistantWidget';
import { setEvent } from 'presentations/views/helpers/analytics';
import AssistantFeature from 'domains/models/subscription/features/assistant';
import { IntegrationType } from 'domains/models/account/providerType';

export interface Props {
  viewer: ViewerItem;
}

export const useDashboardNavigation = (props: Props) => {
  const portalContext = useContext(DashboardPortalContext);
  const assistantWidget = useContext(AssistantWidgetContext);
  const router = useRouter();

  const [to, setTo] = useState<string>(authRedirectUrl(dashboardPath()));
  const [isElectron, setIsElectron] = useState<boolean>(false);
  const [isMacintosh, setIsMacintosh] = useState<boolean>(false);
  const [helpAnchor, setHelpAnchor] = useState<any>(null);
  const [state, loadChat] = useChat();
  const [isiOSApp, setIsiOSApp] = useState<boolean>(false);
  const [activePath, setActivePath] = useState<any>({});

  const onClickChat = (e: SyntheticEvent<HTMLDivElement>) => {
    e.preventDefault();
    const assistantFeature = new AssistantFeature({
      planVersion: props.viewer.planVersion,
    });
    if (!assistantFeature.isValid(props.viewer.planId)) {
      router.push(integrationPath(IntegrationType.Assistant));
      return;
    }
    assistantWidget.dispatch(setChatPopoverAnchor(e.currentTarget));
    setEvent('click chat assistant', {
      position: 'navigation',
    });
  };

  const onCreateProject = async (project: ProjectListItem) => {
    portalContext.dispatch(
      setProjects([...portalContext.state.projects, project]),
    );
  };

  const onDeletedProject = async (projectUid: string) => {
    for (let index = 0; index < portalContext.state.projects.length; index++) {
      const project = portalContext.state.projects[index];
      if (project.uid === projectUid) {
        const newProjects = [...portalContext.state.projects];
        newProjects.splice(index, 1);
        portalContext.dispatch(setProjects(newProjects));
        break;
      }
    }
  };

  const projectForm = useProjectForm({
    teamUid: props.viewer ? props.viewer.team?.uid : undefined,
    onRequested: onCreateProject,
  });

  const updateActivePath = useCallback((pathname: string) => {
    setActivePath(dashboardNavigationActiveList(pathname));
  }, []);

  useEffect(() => {
    updateActivePath(router.asPath);
  }, []);

  const projectsRequest = useRequest(
    props.viewer
      ? apiPath(
          `/v1/teams/${props.viewer.team!.uid}/users/${
            props.viewer.uid
          }/projects`,
        )
      : null,
  );
  useEffect(() => {
    if (projectsRequest.isLoading) return;
    portalContext.dispatch(setProjects(projectsRequest.data));
  }, [projectsRequest.data]);

  const onClickCalendarSetting = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    router.push(calendarIntegrationsPath());
  };

  useEffect(() => {
    if (window.location.pathname !== homePath()) {
      setTo(authRedirectUrl(window.location.pathname + window.location.search));
    }
    setIsElectron(isInElectron());
    const userAgent =
      typeof navigator !== 'undefined' ? navigator.userAgent : '';
    setIsMacintosh(isMacintoshFunc(userAgent));
  }, []);

  useEffect(() => {
    setIsiOSApp(isiOSAppFunc());
  }, []);

  function onClickHelpMenu(e: SyntheticEvent<HTMLAnchorElement>) {
    e.preventDefault();
    e.stopPropagation();
    setHelpAnchor(e.currentTarget);
  }

  const onCloseHelpMenu = () => {
    setHelpAnchor(null);
  };

  const onSelectHelpMenu = async (selectedIndex: number) => {
    setHelpAnchor(null);
    switch (selectedIndex) {
      case 0:
        window.open(helpCenterPath('/', router.locale));
        break;
      case 1:
        loadChat({ open: true });
        break;
      default:
        break;
    }
  };

  return {
    state: {
      isElectron,
      to,
      helpAnchor,
      isMacintosh,
      isiOSApp,
      projectForm: projectForm.state,
      activePath,
      projects: portalContext.state.projects,
      isActiveAssistant: !!assistantWidget.state.chatPopoverAnchor,
    },
    actions: {
      onClickHelpMenu,
      onCloseHelpMenu,
      onSelectHelpMenu,
      onDeletedProject,
      projectForm: projectForm.actions,
      onClickCalendarSetting,
      onClickChat,
    },
  };
};

const dashboardNavigationActiveList = (pathname: string) => {
  return {
    dashboard:
      pathname.match(new RegExp(`${dashboardPath()}[^/]*$`)) !== null ||
      pathname.startsWith(scheduledEventListPath()) ||
      (pathname.startsWith(hostEventTypeListPath()) &&
        !pathname.startsWith(hostEventTypeSettingPath())),
    calendar: pathname.startsWith(dashboardCalendarsPath()),
    integration: pathname.startsWith(integrationListPath()),
    analytics: pathname.startsWith(dashboardStatsPath()),
    search: pathname.startsWith(dashboardSearchPath({})),
    setting:
      pathname.startsWith(accountSettingPath()) ||
      pathname.startsWith(hostEventTypeSettingPath()) ||
      pathname.startsWith(serviceTermListPath()),
    notification:
      pathname.startsWith(notificationsPath()) ||
      pathname.startsWith(messagesPath()),
  };
};
